import {
  AiActivityLogPage,
  AiActivityLogQuery,
} from '@enview/interface/types/triage/AiActivityLog';
import {
  AutoTriageApplyResult,
  AutoTriageApplyScope,
  AutoTriageDemoHeldBill,
  AutoTriageDemoResetResponse,
  AutoTriageDemoSimulateResult,
  AutoTriageHeldCount,
  AutoTriagePendingCounts,
  AutoTriageSettings,
  AutoTriageSettingsInput,
} from '@enview/interface/types/triage/AutoTriage';
import base, { ApiTag } from './base';
import { NEW_BILLS_COUNT_TAG } from './bill';

const autoTriageAPI = base.injectEndpoints({
  endpoints: (build) => ({
    // Fails with 404 when the organization has no Auto Triage settings.
    getAutoTriageSettings: build.query<AutoTriageSettings, { orgId: number }>({
      query: ({ orgId }) => ({
        url: `/v2/organizations/${orgId}/auto-triage/settings`,
        method: 'GET',
      }),
      providesTags: [ApiTag.AUTO_TRIAGE_SETTINGS],
    }),
    // Org admins only. Replaces the rules, thresholds and mode in full; the server answers
    // 400 with a list of messages ("Rule 2: ...") when they're invalid. Saving re-checks held
    // bills, which can move some to Active Review and add activity log entries.
    saveAutoTriageSettings: build.mutation<
      AutoTriageSettings,
      { orgId: number; settings: AutoTriageSettingsInput }
    >({
      query: ({ orgId, settings }) => ({
        url: `/v2/organizations/${orgId}/auto-triage/settings`,
        method: 'PUT',
        data: settings,
      }),
      // Shows the change straight away, and undoes it if the server rejects the settings.
      onQueryStarted: async ({ orgId, settings }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          autoTriageAPI.util.updateQueryData(
            'getAutoTriageSettings',
            { orgId },
            (draft) => {
              Object.assign(draft, settings);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
        }
      },
      invalidatesTags: [
        ApiTag.AUTO_TRIAGE_SETTINGS,
        ApiTag.BILL_TAG,
        ApiTag.AI_ACTIVITY_LOG,
      ],
    }),
    // Shares the settings tag because saving the settings can release held bills.
    getAutoTriageHeldCount: build.query<AutoTriageHeldCount, { orgId: number }>({
      query: ({ orgId }) => ({
        url: `/v2/organizations/${orgId}/auto-triage/held-count`,
        method: 'GET',
      }),
      providesTags: [ApiTag.AUTO_TRIAGE_SETTINGS],
    }),
    // The tags a rule can match: the classifier's topic names.
    getAutoTriageTopics: build.query<string[], void>({
      query: () => ({ url: '/v2/auto-triage/topics', method: 'GET' }),
    }),
    // Bills an Auto Triage click by the user would triage, by scope. Shares the New Bills
    // count tag because the same Workflow Status changes move bills in and out of both.
    // Applying Auto Triage and saving settings invalidate every bill tag, this one included.
    getAutoTriagePendingCounts: build.query<AutoTriagePendingCounts, { orgId: number }>(
      {
        query: ({ orgId }) => ({
          url: `/v2/organizations/${orgId}/auto-triage/pending-counts`,
          method: 'GET',
        }),
        providesTags: [NEW_BILLS_COUNT_TAG],
      },
    ),
    // Applies rule actions to the pending bills in the user's rule workspaces, which can
    // change cached bill data and the What's New counts, and adds activity log entries.
    applyAutoTriage: build.mutation<
      AutoTriageApplyResult,
      { orgId: number; scope: AutoTriageApplyScope }
    >({
      query: ({ orgId, scope }) => ({
        url: `/v2/organizations/${orgId}/auto-triage/apply`,
        method: 'POST',
        data: { scope },
      }),
      invalidatesTags: [ApiTag.BILL_TAG, ApiTag.AI_ACTIVITY_LOG],
    }),
    getAiActivityLog: build.query<
      AiActivityLogPage,
      { orgId: number } & AiActivityLogQuery
    >({
      query: ({ orgId, ...params }) => ({
        url: `/v2/organizations/${orgId}/ai-activity-log`,
        method: 'GET',
        params,
      }),
      providesTags: [ApiTag.AI_ACTIVITY_LOG],
    }),
    // The demo tools below are for Plural staff, and the server only allows them for the
    // demo organizations listed in its settings. Held bills share the settings tag because
    // saving settings, simulating an update and resetting all change which bills are held.
    getAutoTriageDemoHeldBills: build.query<
      AutoTriageDemoHeldBill[],
      { orgId: number }
    >({
      query: ({ orgId }) => ({
        url: '/auto-triage/demo/held-bills',
        method: 'GET',
        params: { orgId },
      }),
      providesTags: [ApiTag.AUTO_TRIAGE_SETTINGS],
    }),
    simulateAutoTriageDemoUpdate: build.mutation<
      AutoTriageDemoSimulateResult,
      { orgId: number; billIds: string[] }
    >({
      query: ({ orgId, billIds }) => ({
        url: '/auto-triage/demo/simulate-update',
        method: 'POST',
        data: { orgId, billIds },
      }),
      invalidatesTags: [
        ApiTag.AUTO_TRIAGE_SETTINGS,
        ApiTag.BILL_TAG,
        ApiTag.AI_ACTIVITY_LOG,
      ],
    }),
    resetAutoTriageDemo: build.mutation<AutoTriageDemoResetResponse, { orgId: number }>(
      {
        query: ({ orgId }) => ({
          url: '/auto-triage/demo/reset',
          method: 'POST',
          data: { orgId },
        }),
        invalidatesTags: [
          ApiTag.AUTO_TRIAGE_SETTINGS,
          ApiTag.BILL_TAG,
          ApiTag.AI_ACTIVITY_LOG,
        ],
      },
    ),
  }),
});

export default autoTriageAPI;
